/* Product Tour — tabbed showcase of Lumetier's three core surfaces.
   Dark Lumetier marketing surface · light inner browser-window mockups
   (because that's exactly what website visitors / clients will see). */

import { useState } from "react";
import { Camera, Calendar, ImagePlus, Check, ChevronRight, MapPin, Clock, Sparkles } from "lucide-react";

const GOLD = "#C9A961";

const HERO_IMG = "https://images.unsplash.com/photo-1519741497674-611481863552?w=1400&q=70";
const PORTFOLIO = [
  "https://images.unsplash.com/photo-1494774157365-9e04c6720e47?w=400&q=70",
  "https://images.unsplash.com/photo-1606800052052-a08af7148866?w=400&q=70",
  "https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=400&q=70",
];

const TABS = [
  { id: "site", label: "Your website", icon: Camera },
  { id: "booking", label: "The booking flow", icon: Calendar },
  { id: "gallery", label: "Client gallery", icon: ImagePlus },
];

export default function ProductTour() {
  const [tab, setTab] = useState("site");

  return (
    <section className="max-w-6xl mx-auto px-6 py-28" id="tour">
      <div className="text-center mb-12">
        <div className="text-[11px] uppercase tracking-[0.32em] text-stone-500 mb-5">A guided tour</div>
        <h2
          className="text-4xl md:text-5xl font-light leading-tight text-stone-100"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
        >
          Three surfaces. One{" "}
          <em className="italic" style={{ color: GOLD }}>cohesive</em> experience.
        </h2>
        <p className="text-stone-400 mt-6 max-w-xl mx-auto text-sm leading-relaxed">
          From the first click on your website to the moment your client picks their favorite photos —
          every screen is designed to feel like one continuous boutique experience.
        </p>
      </div>

      {/* Tab strip (dark) */}
      <div className="flex flex-wrap items-center justify-center gap-2 mb-10" data-testid="tour-tabs">
        {TABS.map((t) => {
          const active = t.id === tab;
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tour-tab-${t.id}`}
              className="inline-flex items-center gap-2 px-5 h-11 text-[11px] uppercase tracking-[0.22em] border transition-colors"
              style={{
                background: active ? "rgba(255,255,255,0.08)" : "transparent",
                color: active ? "#fafaf9" : "#a8a29e",
                borderColor: active ? "rgba(201,169,97,0.5)" : "rgba(255,255,255,0.15)",
              }}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab content — light browser window (this is the real product) */}
      <div className="bg-stone-900 p-2 md:p-4 border border-white/[0.1]" style={{ borderRadius: 4 }}>
        <div className="flex items-center gap-1.5 px-3 py-2">
          <div className="w-2.5 h-2.5 rounded-full bg-stone-700" />
          <div className="w-2.5 h-2.5 rounded-full bg-stone-700" />
          <div className="w-2.5 h-2.5 rounded-full bg-stone-700" />
          <div className="ml-3 text-[10px] text-stone-500 truncate">
            yourstudio.lumetiercreatives.com{tab === "booking" ? "/booking" : tab === "gallery" ? "/gallery/adams-family" : ""}
          </div>
        </div>

        {tab === "site" && <SitePreview />}
        {tab === "booking" && <BookingPreview />}
        {tab === "gallery" && <GalleryStub />}
      </div>
    </section>
  );
}

/* ---------- 1. PUBLIC WEBSITE PREVIEW (light — real product surface) ---------- */
function SitePreview() {
  return (
    <div className="bg-stone-50 overflow-hidden" data-testid="preview-site">
      <div className="px-6 py-4 flex items-center justify-between border-b border-stone-200">
        <div className="text-sm tracking-[0.32em] font-light" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
          YOUR STUDIO
        </div>
        <div className="hidden sm:flex items-center gap-5 text-[10px] uppercase tracking-[0.22em] text-stone-500">
          <span>Services</span>
          <span>Portfolio</span>
          <span>About</span>
          <span className="px-3 h-7 inline-flex items-center bg-stone-900 text-stone-50">Book a session</span>
        </div>
      </div>
      <div className="relative h-72 md:h-96 overflow-hidden">
        <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.2), rgba(0,0,0,0.5))" }}>
          <div className="text-[10px] uppercase tracking-[0.32em] text-stone-200 mb-3">Family · Newborn · Branding</div>
          <h1 className="text-3xl md:text-5xl font-light text-white max-w-2xl leading-tight" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
            Heirloom photography for <em style={{ color: GOLD }}>the moments</em> you&apos;ll remember.
          </h1>
          <div className="h-px w-12 mt-6" style={{ background: GOLD }} />
          <p className="text-stone-200 text-sm mt-4 max-w-md">Boutique studio · Booking now for fall 2026</p>
          <button className="mt-8 px-5 h-10 bg-stone-50 text-stone-900 text-[10px] uppercase tracking-[0.22em] hover:bg-white">
            Book a session →
          </button>
        </div>
      </div>
      <div className="px-6 py-10">
        <div className="text-center text-[10px] uppercase tracking-[0.32em] text-stone-500 mb-6">Signature offerings</div>
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { name: "Family Session", price: 750, len: "90 min", img: PORTFOLIO[0] },
            { name: "Newborn Studio", price: 950, len: "2 hrs", img: PORTFOLIO[1] },
            { name: "Branding Half-Day", price: 1450, len: "4 hrs", img: PORTFOLIO[2] },
          ].map((s) => (
            <div key={s.name} className="bg-white border border-stone-200 overflow-hidden">
              <div className="aspect-[4/3] overflow-hidden">
                <img src={s.img} alt={s.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-4">
                <div className="text-lg" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>{s.name}</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-stone-500 mt-1 flex items-center gap-1.5">
                  <Clock className="w-3 h-3" /> {s.len} · From ${s.price}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------- 2. BOOKING FLOW PREVIEW (light) ---------- */
function BookingPreview() {
  const [step, setStep] = useState(2);
  return (
    <div className="bg-stone-50 px-6 py-8 min-h-[480px]" data-testid="preview-booking">
      <div className="flex items-center justify-center gap-3 mb-8 text-[10px] uppercase tracking-[0.22em]">
        {["Pick a service", "Pick a date", "Confirm & deposit"].map((label, i) => {
          const num = i + 1;
          const active = step === num;
          const done = step > num;
          return (
            <button
              key={label}
              onClick={() => setStep(num)}
              className="flex items-center gap-2"
              data-testid={`booking-step-${num}`}
            >
              <div
                className="w-6 h-6 inline-flex items-center justify-center text-[10px]"
                style={{
                  background: done ? GOLD : active ? "#1c1917" : "transparent",
                  color: done || active ? "#fff" : "#78716c",
                  border: done || active ? "none" : "1px solid #d6d3d1",
                }}
              >
                {done ? <Check className="w-3 h-3" /> : num}
              </div>
              <span style={{ color: active ? "#1c1917" : "#78716c" }}>{label}</span>
              {i < 2 && <ChevronRight className="w-3 h-3 text-stone-300" />}
            </button>
          );
        })}
      </div>

      {step === 1 && (
        <div>
          <div className="text-center mb-6">
            <h3 className="text-2xl font-light" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>Choose your session</h3>
          </div>
          <div className="grid sm:grid-cols-3 gap-3 max-w-3xl mx-auto">
            {[
              { name: "Family Session", price: 750, dep: "30%", len: "90 min", img: PORTFOLIO[0], picked: true },
              { name: "Newborn Studio", price: 950, dep: "30%", len: "2 hrs", img: PORTFOLIO[1] },
              { name: "Branding Half-Day", price: 1450, dep: "50%", len: "4 hrs", img: PORTFOLIO[2] },
            ].map((s) => (
              <div key={s.name} className="bg-white border-2 p-3 cursor-pointer" style={{ borderColor: s.picked ? GOLD : "#e7e5e4" }}>
                <div className="aspect-square mb-3 overflow-hidden">
                  <img src={s.img} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="font-medium text-sm">{s.name}</div>
                <div className="text-xs text-stone-500 mt-1">${s.price} · {s.dep} deposit · {s.len}</div>
                {s.picked && <div className="text-[10px] uppercase tracking-[0.22em] mt-2" style={{ color: GOLD }}>★ Selected</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {step === 2 && (
        <div>
          <div className="text-center mb-6">
            <h3 className="text-2xl font-light" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>Pick your time</h3>
            <p className="text-xs text-stone-500 mt-1">Family Session · 90 min · $750</p>
          </div>
          <div className="max-w-md mx-auto bg-white border border-stone-200 p-5">
            <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-wider text-stone-500 mb-2">
              {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <div key={i} className="py-1">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1 text-center text-xs">
              {Array.from({ length: 35 }).map((_, i) => {
                const day = i - 2;
                const valid = day > 0 && day < 31;
                const isPicked = day === 14;
                const available = valid && [3, 5, 8, 12, 14, 17, 19, 21, 24].includes(day);
                return (
                  <div
                    key={i}
                    className="py-2 cursor-pointer"
                    style={{
                      background: isPicked ? "#1c1917" : available ? "#fffaee" : "transparent",
                      color: isPicked ? "#fff" : available ? "#1c1917" : "#d6d3d1",
                      border: available && !isPicked ? `1px solid ${GOLD}` : "none",
                    }}
                  >
                    {valid ? day : ""}
                  </div>
                );
              })}
            </div>
            <div className="mt-4 pt-4 border-t border-stone-100">
              <div className="text-[10px] uppercase tracking-[0.22em] text-stone-500 mb-2">Times for Aug 14 · 9 slots</div>
              <div className="flex flex-wrap gap-1.5">
                {["9:00", "10:30", "12:00", "2:00", "3:30", "5:00"].map((t, i) => (
                  <div
                    key={t}
                    className="px-2.5 py-1 text-xs border"
                    style={{
                      background: i === 1 ? "#1c1917" : "#fff",
                      color: i === 1 ? "#fff" : "#1c1917",
                      borderColor: i === 1 ? "#1c1917" : "#e7e5e4",
                    }}
                  >
                    {t}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {step === 3 && (
        <div>
          <div className="text-center mb-6">
            <h3 className="text-2xl font-light" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>Confirm & pay deposit</h3>
          </div>
          <div className="max-w-md mx-auto bg-white border border-stone-200 p-6">
            <div className="space-y-2 text-sm pb-4 border-b border-stone-100">
              <div className="flex justify-between"><span className="text-stone-500">Service</span><span>Family Session</span></div>
              <div className="flex justify-between"><span className="text-stone-500">Date</span><span>Aug 14 · 10:30 AM</span></div>
              <div className="flex justify-between"><span className="text-stone-500">Duration</span><span>90 min</span></div>
              <div className="flex justify-between"><span className="text-stone-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> Location</span><span>Studio · Mt View</span></div>
            </div>
            <div className="py-3 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-stone-500">Total</span><span>$750.00</span></div>
              <div className="flex justify-between"><span className="text-stone-500">Deposit (30%)</span><strong>$225.00</strong></div>
              <div className="flex justify-between text-xs text-stone-500"><span>Balance due day of session</span><span>$525.00</span></div>
            </div>
            <div className="pt-4 border-t border-stone-100 space-y-2">
              <div className="text-[10px] uppercase tracking-[0.22em] text-stone-500 mb-2 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Pay deposit via</div>
              <div className="grid grid-cols-2 gap-2">
                <div className="border border-stone-900 px-3 py-2 text-xs flex items-center justify-between"><span>Zelle</span><Check className="w-3.5 h-3.5" /></div>
                <div className="border border-stone-200 px-3 py-2 text-xs text-stone-500">Card</div>
              </div>
              <button className="w-full mt-3 py-3 bg-stone-900 text-stone-50 text-[11px] uppercase tracking-[0.22em]">
                Confirm & send deposit ↗
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="text-center mt-6 text-[10px] uppercase tracking-[0.22em] text-stone-500">
        Click any step above to preview it · Each step is a single page on the live product
      </div>
    </div>
  );
}

/* ---------- 3. GALLERY STUB ---------- */
function GalleryStub() {
  return (
    <div className="bg-stone-50 px-6 py-12 text-center" data-testid="preview-gallery-stub">
      <ImagePlus className="w-10 h-10 mx-auto mb-4" style={{ color: GOLD }} />
      <h3 className="text-2xl font-light mb-2" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
        Scroll down to play with the gallery.
      </h3>
      <p className="text-stone-600 text-sm max-w-md mx-auto mb-6">
        We&apos;ve embedded a fully interactive gallery customizer just below this section — pick your brand color, type your studio name, favorite a few photos.
      </p>
      <a
        href="#customize"
        className="inline-flex items-center gap-2 px-5 h-11 bg-stone-900 text-stone-50 text-[11px] uppercase tracking-[0.22em] hover:bg-stone-800"
      >
        Try the gallery ↓
      </a>
    </div>
  );
}
